import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { Link, useRouter, type ErrorComponentProps } from '@tanstack/react-router';
import { useEffect } from 'react';

import { getErrorMessage } from '@/shared/api/error-messages';
import { hasErrorCode } from '@/shared/api/errors';
import { Button } from '@/shared/ui/button';
import { Result } from '@/shared/ui/result';

/**
 * 화면 로딩 오류 — 다시 시도(쿼리 오류 초기화 + 라우트 재로딩)와 홈으로 가기를 함께 준다.
 * 이용이 중지된 계정은 다시 시도해도 같으므로 로그인 화면으로 안내한다.
 */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const queryErrorResetBoundary = useQueryErrorResetBoundary();

  useEffect(() => {
    queryErrorResetBoundary.reset();
  }, [queryErrorResetBoundary]);

  const isAccountError = hasErrorCode(error, 'AUTH_ACCOUNT_DEACTIVATED');

  return (
    <Result
      tone='danger'
      title='화면을 불러오지 못했어요'
      description={getErrorMessage(error)}
      actions={
        isAccountError ? (
          <Button asChild variant='primary' size='l'>
            <Link to='/login'>로그인 화면으로</Link>
          </Button>
        ) : (
          <>
            <Button asChild variant='secondary' size='l'>
              <Link to='/'>홈으로</Link>
            </Button>
            <Button
              variant='primary'
              size='l'
              onClick={() => {
                reset();
                void router.invalidate();
              }}
            >
              다시 시도하기
            </Button>
          </>
        )
      }
    />
  );
}
